import React, { useState } from 'react';
import { auth } from './firebase';
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, sendEmailVerification,
  signInWithPopup, GoogleAuthProvider,
} from 'firebase/auth';

function friendlyError(code) {
  switch (code) {
    case 'auth/invalid-email': return "That email doesn't look right.";
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential': return 'Wrong email or password.';
    case 'auth/email-already-in-use': return 'That email already has an account. Try signing in.';
    case 'auth/weak-password': return 'Password should be at least 6 characters.';
    case 'auth/operation-not-allowed': return 'Email sign-up is currently unavailable. Use Continue with Google for now.';
    case 'auth/too-many-requests': return 'Too many attempts. Please wait a bit and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request': return '';
    default: return 'Something went wrong. Please try again.';
  }
}

const linkBtn = {
  background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer',
  padding: 0, fontSize: 13, fontFamily: 'var(--font)',
};

// Email/password + Google sign-in. Used logged-out from the homepage and the nav.
export default function AuthModal({ open, onClose, onLogin }) {
  const [mode, setMode] = useState('signin'); // signin | signup | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [info, setInfo] = useState('');

  if (!open) return null;

  const clear = () => { setError(''); setNotice(''); setInfo(''); };
  const done = (user) => { onLogin?.(user); onClose?.(); };

  const handleGoogle = async () => {
    clear(); setBusy(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      done(result.user);
    } catch (e) {
      const msg = friendlyError(e.code);
      if (msg) setError(msg);
    }
    setBusy(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clear(); setBusy(true);
    try {
      if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email.trim());
        setNotice('Password reset email sent. Check your inbox.');
      } else if (mode === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        try { await sendEmailVerification(cred.user); } catch { /* non-blocking */ }
        // Stay signed in (unverified). The app's "Verify your email" screen takes
        // over as the stable destination (with a Resend button) — no sign-out flash.
        // Unverified users are fully gated (rules + createUserProfile require a
        // verified email), so this is safe.
        done(cred.user);
      } else {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
        // Verified -> into the app. Unverified -> the "Verify your email" screen.
        done(cred.user);
      }
    } catch (err) {
      const code = err.code || '';
      const credFail = code === 'auth/invalid-credential' || code === 'auth/user-not-found' || code === 'auth/wrong-password';
      if (mode === 'signin' && credFail) {
        // A failed sign-in is usually a new user who landed on the wrong screen
        // (the common beta case). Route them straight into Create with their email
        // and password kept. Enumeration protection means we can't be sure the
        // account doesn't exist, so we don't claim it does — we cover both cases.
        setMode('signup');
        setError('');
        setInfo("Couldn't sign you in. If you're new, create your account below. Already have an account? Tap Sign in and check your password.");
      } else {
        setError(friendlyError(code) || 'Something went wrong.');
      }
    }
    setBusy(false);
  };

  const title = mode === 'signup' ? 'Create your account' : mode === 'reset' ? 'Reset password' : 'Sign in';

  return (
    // Self-contained overlay (not the shared .modal classes) so the box can't get
    // clamped/collapsed in a web view. Overlay scrolls if the form is tall.
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        overflowY: 'auto', padding: '32px 16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)', padding: 24,
          width: '100%', maxWidth: 380, position: 'relative',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: 12, right: 14, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 20, cursor: 'pointer', lineHeight: 1,
        }}>×</button>

        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{title}</h3>

        {mode !== 'reset' && (
          <>
            <button className="btn" onClick={handleGoogle} disabled={busy}
              style={{ width: '100%', marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0', color: 'var(--muted)', fontSize: 13 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} /> or <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <input className="input" type="email" placeholder="Email" value={email}
            onChange={e => setEmail(e.target.value)} autoComplete="email" required
            style={{ width: '100%', marginBottom: 10 }} />
          {mode !== 'reset' && (
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <input className="input" type={showPw ? 'text' : 'password'} placeholder="Password" value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required
                style={{ width: '100%', paddingRight: 42 }} />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center' }}
              >
                {showPw ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          )}
          {error && <p style={{ color: 'var(--bad)', fontSize: 13, margin: '4px 0' }}>{error}</p>}
          {notice && <p style={{ color: 'var(--good)', fontSize: 13, margin: '4px 0' }}>{notice}</p>}
          {info && <p style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0', lineHeight: 1.5 }}>{info}</p>}
          <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%', marginTop: 6 }}>
            {busy ? <span className="spinner" style={{ width: 16, height: 16, borderTopColor: '#0d0d0d' }} />
              : mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Send reset email' : 'Sign in'}
          </button>
        </form>

        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--muted)', textAlign: 'center' }}>
          {mode === 'signin' && (
            <>
              <button style={linkBtn} onClick={() => { setMode('reset'); clear(); }}>Forgot password?</button>
              <div style={{ marginTop: 8 }}>
                No account? <button style={linkBtn} onClick={() => { setMode('signup'); clear(); }}>Create one</button>
              </div>
            </>
          )}
          {mode === 'signup' && (
            <div>Already have an account? <button style={linkBtn} onClick={() => { setMode('signin'); clear(); }}>Sign in</button></div>
          )}
          {mode === 'reset' && (
            <button style={linkBtn} onClick={() => { setMode('signin'); clear(); }}>Back to sign in</button>
          )}
        </div>
      </div>
    </div>
  );
}
